import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { clearInventorySubmissionResult } from "../features/data/dataSlice";

const SubmissionStatusBanner = () => {
  const dispatch = useDispatch();
  const { isSubmitting, submissionType, result } = useSelector(
    (state) => state.data.inventorySubmission
  );
  const [showResult, setShowResult] = useState(false);

  // Show result banner — longer for failures so the rep has time to read it.
  useEffect(() => {
    if (result) {
      setShowResult(true);
      const delay = result.success ? 4000 : 12000;
      const timer = setTimeout(() => {
        setShowResult(false);
        dispatch(clearInventorySubmissionResult());
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [result, dispatch]);

  // Don't render if nothing to show
  if (!isSubmitting && !showResult) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {isSubmitting && (
        <div className="bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg min-w-[280px]">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
            <span className="font-medium text-sm">
              {submissionType === 'submit' ? 'Submitting inventory...' : 'Saving inventory...'}
            </span>
          </div>
        </div>
      )}

      {/*
        CORRECTED 2026-08-27 (thread #1233): a PARTIAL result is not a failure.
        Before this, "53 of your 54 items saved, 1 is still on your list" painted
        the same solid red as "nothing saved" — the rep reads red and assumes all
        the work was lost, re-does everything, and reports it as a glitch. Partial
        now renders amber, names the count, and lists the items that are still
        outstanding so the rep knows exactly what is left.
      */}
      {showResult && result && (
        <div
          className={`px-4 py-3 rounded-lg shadow-lg max-w-sm ${
            result.success
              ? 'bg-green-600 text-white'
              : result.partial
                ? 'bg-amber-500 text-white'
                : 'bg-red-600 text-white'
          }`}
        >
          <div className="flex items-start gap-3">
            {result.success ? (
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.293 4.293a1 1 0 011.414 0l7 7a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7a1 1 0 010-1.414l7-7z" />
              </svg>
            )}
            <div className="flex-1">
              <p className="font-medium text-sm">{result.message}</p>
              {!result.success && result.failedItems?.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs leading-snug list-disc list-inside">
                  {result.failedItems.slice(0, 5).map((item, i) => (
                    <li key={i}>
                      {typeof item === 'string' ? item : item.itemName}
                      {typeof item !== 'string' && item.reason ? ` — ${item.reason}` : ''}
                    </li>
                  ))}
                  {result.failedItems.length > 5 && (
                    <li>and {result.failedItems.length - 5} more still on your list</li>
                  )}
                </ul>
              )}
            </div>
            {!result.success && (
              <button
                onClick={() => { setShowResult(false); dispatch(clearInventorySubmissionResult()); }}
                className="text-white opacity-70 hover:opacity-100 ml-1 flex-shrink-0"
                aria-label="Dismiss"
              >✕</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionStatusBanner;
